import React from 'react';
import { tv, type VariantProps } from 'tailwind-variants';

// Define spinner variants using tailwind-variants
const spinner = tv({
  base: 'flex flex-col items-center justify-center',
  variants: {
    size: {
      xs: { container: 'w-4 h-4', text: 'text-xs mt-1' },
      s: { container: 'w-6 h-6', text: 'text-xs mt-2' },
      md: { container: 'w-10 h-10', text: 'text-sm mt-2' },
      lg: { container: 'w-16 h-16', text: 'text-base mt-3' },
    },
    colorScheme: {
      primary: 'var(--primaryColor500)',
      teal: 'var(--tealColor700)',
      success: 'var(--sematicSuccessColor)',
      info: 'var(--sematicInfoColor)',
      error: 'var(--sematicErrorColor)',
      gray: 'var(--textGray)',
    },
    overlay: {
      true: 'pointer-events-none z-20',
      false: '',
    },
  },
  defaultVariants: {
    size: 'md',
    colorScheme: 'teal',
    overlay: false,
  },
});

// Type definition for component props
type SpinnerLoaderProps = VariantProps<typeof spinner> & {
  text?: string;
  className?: string;
  customColor?: string;
  textColor?: string;
};

const SpinnerLoader: React.FC<SpinnerLoaderProps> = ({
  size = 'md',
  colorScheme = 'teal',
  overlay = false,
  text,
  className = '',
  customColor,
  textColor = 'var(--textBlack)',
}) => {
  const styles = spinner({ size, overlay, className });

  // Get container size based on variant
  const containerSize = spinner.variants.size[size].container;
  const textSize = spinner.variants.size[size].text;

  // Get color (either custom or from the predefined color scheme)
  const color = customColor || spinner.variants.colorScheme[colorScheme];

  // Get actual pixel size for calculations
  const pixelSize = size === 'xs' ? 16 : size === 's' ? 24 : size === 'md' ? 40 : 64;

  // Calculate segment dimensions - match the image more precisely
  const capsuleWidth = pixelSize * 0.2;
  const capsuleHeight = pixelSize * 0.35;
  const radius = pixelSize * 0.4; // Adjust radius to match the image

  return (
    <div className={styles}>
      {/* Only the container spins, not individual segments */}
      <div className={`${containerSize} relative`} style={{ animation: 'spin 1.3s linear infinite' }}>
        {/* Create 8 segments arranged in a circle */}
        {[...Array(8)].map((_, index) => {
          return (
            <div
              key={`segment-${index}`}
              className='absolute rounded-full'
              style={{
                width: capsuleWidth,
                height: capsuleHeight,
                backgroundColor: color,
                top: '50%',
                left: '50%',
                marginLeft: -capsuleWidth / 2,
                marginTop: -capsuleHeight / 2,
                transform: `rotate(${index * 45}deg) translateY(-${radius}px)`,
                // Adjust opacity to match the image better
                opacity: (index - 1) * 0.1 + 0.2, // Adjust opacity for each segment
              }}
            />
          );
        })}
      </div>

      {text && (
        <span className={`${textSize} text-center font-medium`} style={{ color: textColor }}>
          {text}
        </span>
      )}
    </div>
  );
};

export default SpinnerLoader;
